// One call that wires the input pieces a game needs from its contract: the controls table, the actions (with the
// universal buffer), the rebinding model over a storage, the contexts stack, and hold modes kept in step with the
// model. Prompts read the device used last and a glyph table the game passes. `checkTouchContract` ties the touch
// layout (data) to the contract: every touch binding is something the layout writes, and the other way round.
import { Actions, type HoldMode } from "./actions";
import type { BufferOptions } from "./buffer";
import { InputContexts } from "./contexts";
import { DEVICE_FAMILIES, checkContract, glyphFor, shapeOf, type BindingDecl, type CheckOptions, type ContractCheck, type DeviceFamily, type InputContract } from "./contract";
import { Controls } from "./controls";
import { Rebinding, type StorageLike } from "./rebind";
import { touchControls, type TouchLayoutSpec } from "./touch-layout";

export interface InputOptions {
  /** The game's contract (its defaults). */
  contract: InputContract;
  /** Sim time, seconds (injected holds run on it). */
  now: () => number;
  /** The sim step, seconds (the buffer ages by it when the runtime doesn't pass one). */
  dt?: number;
  /** Which families the contract must cover. */
  check?: CheckOptions;
  /** The universal input buffer's actions and hit-stop predicate. */
  buffer?: BufferOptions;
  /** Where binding overrides and hold modes are kept (null: memory only). */
  storage?: StorageLike | null;
  storageKey?: string;
  /** The game's default hold modes (from its settings). */
  holdModes?: Readonly<Record<string, HoldMode>>;
  /** An existing controls table to read (default a new one on `now`). */
  controls?: Controls;
}

/** What `createInput` returns. */
export interface Input {
  readonly controls: Controls;
  readonly actions: Actions;
  readonly contexts: InputContexts;
  readonly rebinding: Rebinding;
  /** The effective contract's check (run again after every rebinding). */
  check(): ContractCheck;
  /** A prompt's glyph for `action` on `family` (default: the device used last, else keyboard and mouse), falling
   *  back through the other families when the action has no glyph there. `tables` maps family → glyph key → glyph. */
  glyph<G>(action: string, tables: Readonly<Partial<Record<DeviceFamily, Readonly<Record<string, G>>>>>, family?: DeviceFamily): { family: DeviceFamily; binding: BindingDecl; key: string; glyph: G } | null;
}

/** Wire the input pieces from a contract. Register the step boundaries with `bindActions(runtime, input.actions)`. */
export function createInput(o: InputOptions): Input {
  const controls = o.controls ?? new Controls(o.now);
  const rebinding = new Rebinding({ contract: o.contract, storage: o.storage, key: o.storageKey, check: o.check, holdModes: o.holdModes });
  const actions = new Actions({}, { controls, now: o.now, buffer: o.buffer, dt: o.dt });
  const contexts = new InputContexts(rebinding.effective(), actions);
  const syncModes = (): void => {
    for (const a of o.contract.actions) if (a.holdOrToggle) actions.setHoldMode(a.id, rebinding.holdMode(a.id));
  };
  syncModes();
  let checked = checkContract(rebinding.effective(), o.check);
  rebinding.onChange((eff) => {
    contexts.setContract(eff);
    syncModes();
    checked = checkContract(eff, o.check);
  });
  return {
    controls,
    actions,
    contexts,
    rebinding,
    check: () => checked,
    glyph(action, tables, family) {
      const first = family ?? controls.device ?? "keyboardMouse";
      for (const f of [first, ...DEVICE_FAMILIES.filter((x) => x !== first)]) {
        const t = tables[f];
        const g = t ? glyphFor(rebinding.effective(), action, f, t) : null;
        if (g) return { family: f, ...g };
      }
      return null;
    },
  };
}

/** Problems between a touch layout and the contract: a touch binding (in the contexts the layer serves) that the
 *  layout doesn't write, of the right kind, and a layout control no action binds. Empty when they agree. */
export function checkTouchContract(c: InputContract, layout: TouchLayoutSpec, contexts?: readonly string[]): string[] {
  const writes = touchControls(layout);
  const out: string[] = [];
  const bound = new Set<string>();
  const actions = new Map(c.actions.map((a) => [a.id, a]));
  for (const b of c.bindings) {
    if (b.device !== "touch") continue;
    const a = actions.get(b.action);
    if (!a) continue;
    for (const ctl of b.controls) bound.add(ctl);
    if (contexts && !contexts.includes(a.context)) continue;
    const shape = shapeOf(b, a.type);
    const list = shape === "button" || shape === "dpad" ? writes.buttons : shape === "pointer" ? writes.deltas : writes.vectors;
    for (const ctl of b.controls) if (ctl && !list.includes(ctl)) out.push(`touch binding "${b.id}": the layout writes no ${shape === "pointer" ? "drag" : shape === "button" || shape === "dpad" ? "button" : "stick"} "${ctl}"`);
  }
  for (const ctl of [...writes.buttons, ...writes.vectors, ...writes.deltas]) if (!bound.has(ctl)) out.push(`touch layout control "${ctl}" is bound to no action`);
  return out;
}
